import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, type WatchQueryFetchPolicy } from "@apollo/client";
import { GET_TICKET_MANAGERS } from "../TicketingAPI";
import { useIcon } from "@SeemplicityDev/shared";

export interface UseTicketManagersOptions {
  context: string;
  type?: string;
  ticketProviderDefinitionId?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
}

// Raw provider as returned by GET_TICKET_MANAGERS -> ticket_managers[].providers[].
interface TicketManagerProviderResponse {
  id?: string;
  display?: string;
  known_identifier?: string;
}

// Raw ticket manager entry as returned by GET_TICKET_MANAGERS -> ticket_managers[].
interface TicketManagerResponse {
  type?: string;
  status?: string;
  ticket_provider_definition_id?: string;
  endpoint_type?: string;
  implements_column_mapping?: boolean;
  must_have_mapping?: boolean;
  friendly_name?: string;
  providers?: TicketManagerProviderResponse[];
  attachments_supported?: boolean;
}

interface GetTicketManagersData {
  ticket_managers?: TicketManagerResponse[];
}

// Provider shape after mapping (consumed by callers of useTicketManagers).
export interface TicketManagerProvider {
  name?: string;
  id?: string;
}

// Mapped ticket manager shape returned from the hook.
export interface TicketManager {
  name?: string;
  label: string;
  value?: string;
  friendlyName?: string;
  mustHaveMapping?: boolean;
  mustHaveColumnsMapping?: boolean;
  type?: string;
  leftImg?: ReturnType<ReturnType<typeof useIcon>["getDataSourceIcon"]>;
  providers?: TicketManagerProvider[];
  attachmentSupported?: boolean;
  ticketProviderDefinitionId?: string;
}

export interface UseTicketManagersReturn {
  getTicketManagerProps: (value?: string) => TicketManager | undefined;
  loading: boolean;
  error: ReturnType<typeof useQuery>["error"];
  ticketManagers: TicketManager[] | null;
  hasIntegratedProviders?: boolean;
}

export const useTicketManagers = ({
  context,
  type,
  ticketProviderDefinitionId,
  fetchPolicy,
}: UseTicketManagersOptions): UseTicketManagersReturn => {
  const { getDataSourceIcon } = useIcon();

  const [ticketManagers, setTicketManagers] = useState<TicketManager[] | null>(
    null
  );

  const {
    data: ticketProvidersData,
    loading,
    error,
  } = useQuery<GetTicketManagersData>(GET_TICKET_MANAGERS, {
    variables: {
      providers_query_context: context,
      ...((type || ticketProviderDefinitionId) && {
        filters: {
          ...(type && { type }),
          ...(ticketProviderDefinitionId && {
            ticket_provider_definition_id: ticketProviderDefinitionId,
          }),
        },
      }),
    },
    ...(fetchPolicy && { fetchPolicy }),
  });

  useEffect(() => {
    if (!!ticketProvidersData) {
      const ticketManagersArray = ticketProvidersData?.ticket_managers
        // Filter out display-only providers (ACTIVE_CS) — mirrors DataSourcesUtils ACTIVE_CS pattern.
        ?.filter((ticketManager) => ticketManager?.status !== "ACTIVE_CS")
        ?.map((ticketManager) => {
          // Original JS passed `ticketManager?.type` (possibly undefined)
          // directly; cast preserves that exact runtime call. getDataSourceIcon
          // handles missing keys by returning the unknown icon.
          const leftImg = getDataSourceIcon(
            ticketManager?.type as string,
            "L"
          );

          return {
            name: ticketManager?.type,
            label: "",
            value: ticketManager?.type,
            friendlyName: ticketManager?.friendly_name,
            mustHaveMapping: ticketManager?.must_have_mapping,
            mustHaveColumnsMapping: ticketManager?.implements_column_mapping,
            type: ticketManager?.endpoint_type,
            leftImg,
            providers: ticketManager?.providers?.map((ticketProvider) => ({
              name: ticketProvider?.display,
              id: ticketProvider?.id,
            })),
            attachmentSupported: ticketManager?.attachments_supported,
            ticketProviderDefinitionId:
              ticketManager?.ticket_provider_definition_id,
          };
        });

      setTicketManagers(ticketManagersArray ?? null);
    }
  }, [ticketProvidersData]);

  const getTicketManagerProps = useCallback(
    (value?: string) =>
      ticketManagers?.find((ticketManager) => ticketManager?.value === value),
    [ticketManagers]
  );

  const hasIntegratedProviders = useMemo(() => {
    return ticketManagers?.some(
      (ticketManager) => !!ticketManager?.providers?.length
    );
  }, [ticketManagers]);

  return {
    getTicketManagerProps,
    loading,
    error,
    ticketManagers,
    hasIntegratedProviders,
  };
};

export default useTicketManagers;
